<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import PageLayout from 'dashboard/components-next/agents/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();
// Confirmação do sistema (no lugar do confirm() do navegador).
const confirmRef = ref(null);

const accountId = computed(() => route.params.accountId);
const agentId = computed(() => route.params.agentId);

const getAgent = useMapGetter('aiAgents/getRecord');
const agent = computed(() => getAgent.value(agentId.value));

const rules = useMapGetter('aiRules/getRules');
const rulesUiFlags = useMapGetter('aiRules/getUIFlags');

const isFetching = computed(() => rulesUiFlags.value.fetchingList);
const isCreating = computed(() => rulesUiFlags.value.creating);
const isUpdating = computed(() => rulesUiFlags.value.updating);

const ruleDialogRef = ref(null);
const editingRule = ref(null);
const deletingRuleId = ref(null);

// O que o MOTOR de regras entende (rules_evaluation_service): condição
// {field, operator, value}, ação por tipo e horário {start_time, end_time,
// days}. O formulário abaixo monta exatamente esses shapes — antes ele só
// salvava nome/tipo e a regra nascia vazia (sem efeito).
const CONDITION_FIELDS = [
  'message_content',
  'keywords',
  'first_message',
  'message_count',
  'contact_label',
  'conversation_label',
  'inbox_name',
  'sentiment',
  'conversation_status',
  'priority',
];
const OPERATORS = [
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts_with',
  'ends_with',
  'greater_than',
  'less_than',
  'is_empty',
  'is_not_empty',
];
const NO_VALUE_OPERATORS = ['is_empty', 'is_not_empty'];
const ACTIONS_BY_TYPE = {
  trigger: ['set_context'],
  filter: ['ignore', 'send_auto_reply'],
  routing: ['add_label', 'set_priority'],
  escalation: ['transfer_to_human', 'send_escalation_message'],
  schedule: [],
};
const MESSAGE_ACTIONS = [
  'set_context',
  'send_auto_reply',
  'send_escalation_message',
];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const WEEK_DAYS = [
  { key: 'MON', value: 'Seg' },
  { key: 'TUE', value: 'Ter' },
  { key: 'WED', value: 'Qua' },
  { key: 'THU', value: 'Qui' },
  { key: 'FRI', value: 'Sex' },
  { key: 'SAT', value: 'Sáb' },
  { key: 'SUN', value: 'Dom' },
];

const emptyForm = () => ({
  name: '',
  rule_type: 'trigger',
  conditions: { field: '', operator: 'contains', value: '' },
  schedule: {
    start_time: '08:00',
    end_time: '18:00',
    days: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'],
  },
  actions: { action: 'set_context', message: '', label: '', priority: 'high' },
  priority: 0,
  active: true,
});

const formData = ref(emptyForm());

const backUrl = computed(() => ({
  name: 'ai_agents_settings',
  params: { accountId: accountId.value, agentId: agentId.value },
}));

const ruleTypes = computed(() => [
  { value: 'trigger', label: t('AI_AGENTS.RULES.TYPES.TRIGGER.LABEL') },
  { value: 'filter', label: t('AI_AGENTS.RULES.TYPES.FILTER.LABEL') },
  { value: 'routing', label: t('AI_AGENTS.RULES.TYPES.ROUTING.LABEL') },
  { value: 'schedule', label: t('AI_AGENTS.RULES.TYPES.SCHEDULE.LABEL') },
  { value: 'escalation', label: t('AI_AGENTS.RULES.TYPES.ESCALATION.LABEL') },
]);

const selectedTypeDescription = computed(() =>
  t(
    `AI_AGENTS.RULES.TYPES.${formData.value.rule_type.toUpperCase()}.DESCRIPTION`
  )
);

const isSchedule = computed(() => formData.value.rule_type === 'schedule');
const actionOptions = computed(
  () => ACTIONS_BY_TYPE[formData.value.rule_type] || []
);
const needsMessage = computed(() =>
  MESSAGE_ACTIONS.includes(formData.value.actions.action)
);
const needsValue = computed(
  () => !NO_VALUE_OPERATORS.includes(formData.value.conditions.operator)
);

const onTypeChange = type => {
  formData.value.rule_type = type;
  const first = (ACTIONS_BY_TYPE[type] || [])[0] || '';
  formData.value.actions = { ...formData.value.actions, action: first };
};

const toggleDay = day => {
  const days = formData.value.schedule.days;
  formData.value.schedule.days = days.includes(day)
    ? days.filter(d => d !== day)
    : [...days, day];
};

const fieldLabel = field =>
  t(`AI_AGENTS.RULES.CONDITION_FIELDS.${field.toUpperCase()}`);
const operatorLabel = op => t(`AI_AGENTS.RULES.OPERATORS.${op.toUpperCase()}`);
const actionLabel = action =>
  t(
    `AI_AGENTS.RULES.ACTIONS.${formData.value.rule_type.toUpperCase()}.${action.toUpperCase()}`
  );

onMounted(async () => {
  await store.dispatch('aiAgents/show', agentId.value);
  await store.dispatch('aiRules/get', { agentId: agentId.value });
});

const openCreateDialog = () => {
  editingRule.value = null;
  formData.value = { ...emptyForm(), priority: rules.value.length };
  ruleDialogRef.value?.open();
};

const openEditDialog = rule => {
  editingRule.value = rule;
  const base = emptyForm();
  const conditions = rule.conditions || {};
  const actions = rule.actions || {};
  formData.value = {
    name: rule.name,
    rule_type: rule.rule_type,
    conditions: {
      field: conditions.field || '',
      operator: conditions.operator || 'contains',
      value: conditions.value || '',
    },
    schedule: {
      start_time: conditions.start_time || base.schedule.start_time,
      end_time: conditions.end_time || base.schedule.end_time,
      days: conditions.days || base.schedule.days,
    },
    actions: {
      action:
        actions.action || (ACTIONS_BY_TYPE[rule.rule_type] || [])[0] || '',
      message: actions.message || '',
      label: actions.params?.label || '',
      priority: actions.params?.priority || 'high',
    },
    priority: rule.priority,
    active: rule.active,
  };
  // Gatilho antigo salvava só message (sem action)
  if (rule.rule_type === 'trigger' && actions.message) {
    formData.value.actions.action = 'set_context';
  }
  ruleDialogRef.value?.open();
};

const closeRuleDialog = () => {
  ruleDialogRef.value?.close();
  editingRule.value = null;
};

// Payload no shape do motor: horário vai em conditions; condição sem campo
// vale para todas as mensagens; a ação leva só o que usa.
const buildPayload = () => {
  const f = formData.value;
  let conditions = {};
  if (f.rule_type === 'schedule') {
    conditions = { ...f.schedule };
  } else if (f.conditions.field) {
    conditions = {
      field: f.conditions.field,
      operator: f.conditions.operator,
      value: NO_VALUE_OPERATORS.includes(f.conditions.operator)
        ? ''
        : f.conditions.value,
    };
  }

  let actions = {};
  if (f.actions.action === 'set_context') {
    // O motor lê trigger via actions.message direto
    actions = { message: f.actions.message };
  } else if (f.actions.action) {
    actions = { action: f.actions.action };
    if (MESSAGE_ACTIONS.includes(f.actions.action)) {
      actions.message = f.actions.message;
    }
    if (f.actions.action === 'add_label') {
      actions.params = { label: f.actions.label };
    }
    if (f.actions.action === 'set_priority') {
      actions.params = { priority: f.actions.priority };
    }
  }

  return {
    name: f.name,
    rule_type: f.rule_type,
    conditions,
    actions,
    priority: f.priority,
    active: f.active,
  };
};

const handleSaveRule = async () => {
  if (!formData.value.name.trim()) {
    useAlert(t('AI_AGENTS.RULES.VALIDATION.NAME_REQUIRED'));
    return;
  }

  try {
    const data = buildPayload();
    if (editingRule.value) {
      await store.dispatch('aiRules/update', {
        agentId: agentId.value,
        ruleId: editingRule.value.id,
        data,
      });
      useAlert(t('AI_AGENTS.RULES.MESSAGES.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('aiRules/create', {
        agentId: agentId.value,
        data,
      });
      useAlert(t('AI_AGENTS.RULES.MESSAGES.CREATE_SUCCESS'));
    }
    closeRuleDialog();
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.RULES.MESSAGES.SAVE_ERROR'));
  }
};

const handleDeleteRule = async rule => {
  const ok = await confirmRef.value?.ask({
    title: t('AI_AGENTS.CONFIRM_DIALOG.DELETE_RULE_TITLE'),
    description: t('AI_AGENTS.RULES.CONFIRM_DELETE', { name: rule.name }),
    confirmLabel: t('AI_AGENTS.CONFIRM_DIALOG.DELETE_CONFIRM'),
  });
  if (!ok) return;
  try {
    deletingRuleId.value = rule.id;
    await store.dispatch('aiRules/delete', {
      agentId: agentId.value,
      ruleId: rule.id,
    });
    useAlert(t('AI_AGENTS.RULES.MESSAGES.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.RULES.MESSAGES.DELETE_ERROR'));
  } finally {
    deletingRuleId.value = null;
  }
};

const toggleRuleActive = async rule => {
  try {
    await store.dispatch('aiRules/update', {
      agentId: agentId.value,
      ruleId: rule.id,
      data: { active: !rule.active },
    });
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.RULES.MESSAGES.UPDATE_ERROR'));
  }
};

const getRuleTypeLabel = type => {
  const found = ruleTypes.value.find(item => item.value === type);
  return found?.label || type;
};

const getRuleTypeIcon = type => {
  const icons = {
    trigger: 'i-lucide-zap',
    filter: 'i-lucide-filter',
    routing: 'i-lucide-arrow-right-left',
    schedule: 'i-lucide-clock',
    escalation: 'i-lucide-arrow-up-from-line',
  };
  return icons[type] || 'i-lucide-list-checks';
};

// Resumo humano da regra na listagem: "Campo contém 'valor'" / dias e faixa.
const ruleSummary = rule => {
  const c = rule.conditions || {};
  if (rule.rule_type === 'schedule' && c.start_time) {
    return `${(c.days || []).join(', ')} · ${c.start_time}–${c.end_time}`;
  }
  if (c.field) {
    const value = c.value ? ` "${c.value}"` : '';
    return `${fieldLabel(c.field)} ${operatorLabel(c.operator || 'contains')}${value}`;
  }
  return t('AI_AGENTS.RULES.FORM.NO_CONDITION');
};
</script>

<template>
  <PageLayout
    :header-title="t('AI_AGENTS.RULES.TITLE')"
    :back-url="backUrl"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    full-width
  >
    <template #header>
      <Button
        :label="t('AI_AGENTS.RULES.CREATE_RULE')"
        icon="i-lucide-plus"
        @click="openCreateDialog"
      />
    </template>

    <template #body>
      <div class="w-full max-w-4xl mx-auto pb-8 px-4 sm:px-6">
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('AI_AGENTS.RULES.DESCRIPTION', { name: agent?.name }) }}
        </p>

        <!-- Como funciona: cada tipo em uma linha, sempre visível -->
        <div
          class="mb-6 rounded-xl border border-n-weak bg-n-solid-1 p-4 text-xs"
        >
          <p class="mb-2 font-medium text-n-slate-12">
            {{ t('AI_AGENTS.RULES.INFO_BOX.TITLE') }}
          </p>
          <div class="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            <p
              v-for="ruleType in ruleTypes"
              :key="ruleType.value"
              class="flex items-center gap-2 text-n-slate-11"
            >
              <span
                :class="getRuleTypeIcon(ruleType.value)"
                class="size-3.5 shrink-0 text-n-slate-10"
              />
              <span class="font-medium text-n-slate-12">
                {{ ruleType.label }}:
              </span>
              {{
                t(`AI_AGENTS.RULES.INFO_BOX.${ruleType.value.toUpperCase()}`)
              }}
            </p>
          </div>
        </div>

        <!-- Rules List -->
        <div v-if="rules.length > 0" class="space-y-3">
          <div
            v-for="rule in rules"
            :key="rule.id"
            class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl"
            :class="{ 'opacity-60': !rule.active }"
          >
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center"
                :class="rule.active ? 'bg-n-brand/10' : 'bg-n-alpha-2'"
              >
                <span
                  class="w-5 h-5"
                  :class="[
                    getRuleTypeIcon(rule.rule_type),
                    rule.active ? 'text-n-brand' : 'text-n-slate-11',
                  ]"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <h4 class="text-sm font-medium text-n-slate-12 truncate">
                    {{ rule.name }}
                  </h4>
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ getRuleTypeLabel(rule.rule_type) }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-11 truncate mt-0.5">
                  {{ ruleSummary(rule) }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 ml-4">
              <Switch
                :model-value="rule.active"
                @update:model-value="toggleRuleActive(rule)"
              />
              <Button
                v-tooltip="t('AI_AGENTS.ACTIONS.EDIT')"
                icon="i-lucide-pencil"
                variant="ghost"
                size="sm"
                @click="openEditDialog(rule)"
              />
              <Button
                v-tooltip="t('AI_AGENTS.ACTIONS.DELETE')"
                icon="i-lucide-trash-2"
                variant="ghost"
                size="sm"
                color="ruby"
                :is-loading="deletingRuleId === rule.id"
                @click="handleDeleteRule(rule)"
              />
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <div
            class="w-16 h-16 mb-4 rounded-xl bg-n-alpha-2 flex items-center justify-center"
          >
            <span class="i-lucide-list-checks text-2xl text-n-slate-11" />
          </div>
          <h3 class="text-base font-medium text-n-slate-12 mb-2">
            {{ t('AI_AGENTS.RULES.EMPTY.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-6 max-w-sm">
            {{ t('AI_AGENTS.RULES.EMPTY.DESCRIPTION') }}
          </p>
          <Button
            :label="t('AI_AGENTS.RULES.CREATE_FIRST')"
            icon="i-lucide-plus"
            @click="openCreateDialog"
          />
        </div>
      </div>
    </template>
  </PageLayout>

  <!-- Rule Dialog -->
  <Dialog
    ref="ruleDialogRef"
    overflow-y-auto
    :title="
      editingRule
        ? t('AI_AGENTS.RULES.EDIT_RULE')
        : t('AI_AGENTS.RULES.CREATE_RULE')
    "
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="closeRuleDialog"
  >
    <div class="py-4 space-y-4">
      <Input
        v-model="formData.name"
        :label="t('AI_AGENTS.RULES.FORM.NAME')"
        :placeholder="t('AI_AGENTS.RULES.FORM.NAME_PLACEHOLDER')"
        required
      />

      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('AI_AGENTS.RULES.FORM.TYPE') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="ruleType in ruleTypes"
            :key="ruleType.value"
            type="button"
            class="px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors"
            :class="
              formData.rule_type === ruleType.value
                ? 'bg-n-brand text-white border-n-brand'
                : 'bg-n-solid-1 text-n-slate-11 border-n-weak hover:border-n-slate-7'
            "
            @click="onTypeChange(ruleType.value)"
          >
            {{ ruleType.label }}
          </button>
        </div>
        <p class="mt-1.5 text-xs text-n-slate-10">
          {{ selectedTypeDescription }}
        </p>
      </div>

      <!-- Horário (schedule): dias + faixa -->
      <div v-if="isSchedule" class="rounded-lg border border-n-weak p-3">
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.RULES.SCHEDULE_FIELDS.DAYS_OF_WEEK') }}
        </p>
        <div class="mb-3 flex flex-wrap gap-1.5">
          <button
            v-for="day in WEEK_DAYS"
            :key="day.key"
            type="button"
            class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
            :class="
              formData.schedule.days.includes(day.value)
                ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
            "
            @click="toggleDay(day.value)"
          >
            {{ t(`AI_AGENTS.RULES.SCHEDULE_FIELDS.DAYS.${day.key}`) }}
          </button>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ t('AI_AGENTS.RULES.SCHEDULE_FIELDS.START_TIME') }}
            </label>
            <input
              v-model="formData.schedule.start_time"
              type="time"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ t('AI_AGENTS.RULES.SCHEDULE_FIELDS.END_TIME') }}
            </label>
            <input
              v-model="formData.schedule.end_time"
              type="time"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </div>
        </div>
      </div>

      <!-- Condição (demais tipos) -->
      <div v-else class="rounded-lg border border-n-weak p-3">
        <p class="mb-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.RULES.CONDITION_SECTION.TITLE') }}
        </p>
        <p class="mb-2 text-xs text-n-slate-10">
          {{ t('AI_AGENTS.RULES.FORM.NO_CONDITION_HINT') }}
        </p>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <select
            v-model="formData.conditions.field"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">
              {{ t('AI_AGENTS.RULES.CONDITION_SECTION.SELECT') }}
            </option>
            <option
              v-for="field in CONDITION_FIELDS"
              :key="field"
              :value="field"
            >
              {{ fieldLabel(field) }}
            </option>
          </select>
          <select
            v-model="formData.conditions.operator"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :disabled="!formData.conditions.field"
          >
            <option v-for="op in OPERATORS" :key="op" :value="op">
              {{ operatorLabel(op) }}
            </option>
          </select>
          <input
            v-if="needsValue"
            v-model="formData.conditions.value"
            type="text"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('AI_AGENTS.RULES.CONDITION_SECTION.VALUE')"
            :disabled="!formData.conditions.field"
          />
        </div>
      </div>

      <!-- Ação -->
      <div
        v-if="actionOptions.length"
        class="rounded-lg border border-n-weak p-3"
      >
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.RULES.ACTION_SECTION.TITLE') }}
        </p>
        <select
          v-model="formData.actions.action"
          class="reset-base mb-2 w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option v-for="action in actionOptions" :key="action" :value="action">
            {{ actionLabel(action) }}
          </option>
        </select>
        <TextArea
          v-if="needsMessage"
          v-model="formData.actions.message"
          :label="t('AI_AGENTS.RULES.ACTION_SECTION.MESSAGE')"
          :placeholder="t('AI_AGENTS.RULES.ACTION_SECTION.MESSAGE_PLACEHOLDER')"
          :rows="2"
        />
        <Input
          v-if="formData.actions.action === 'add_label'"
          v-model="formData.actions.label"
          :label="t('AI_AGENTS.RULES.ACTIONS.ROUTING.ADD_LABEL')"
          :placeholder="t('AI_AGENTS.RULES.FORM.LABEL_PLACEHOLDER')"
        />
        <select
          v-if="formData.actions.action === 'set_priority'"
          v-model="formData.actions.priority"
          class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option v-for="p in PRIORITIES" :key="p" :value="p">
            {{ t(`AI_AGENTS.RULES.PRIORITIES.${p.toUpperCase()}`) }}
          </option>
        </select>
      </div>

      <div
        class="flex items-center justify-between p-3 bg-n-alpha-1 rounded-lg"
      >
        <span class="text-sm text-n-slate-12">
          {{ t('AI_AGENTS.RULES.FORM.ACTIVE') }}
        </span>
        <Switch v-model="formData.active" />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          :label="t('AI_AGENTS.ACTIONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="closeRuleDialog"
        />
        <Button
          :label="
            editingRule
              ? t('AI_AGENTS.ACTIONS.SAVE')
              : t('AI_AGENTS.ACTIONS.CREATE')
          "
          :is-loading="isCreating || isUpdating"
          @click="handleSaveRule"
        />
      </div>
    </template>
  </Dialog>
  <ConfirmDialog ref="confirmRef" />
</template>
